import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Smartphone, Chrome, Share, Plus, Download, CheckCircle2, X } from 'lucide-react';
import { logger } from '../utils/logger';

const InstallPWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [deviceType, setDeviceType] = useState('unknown');

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsInstalled(true);
    }

    // Detect device type
    const userAgent = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(userAgent)) {
      setDeviceType('ios');
    } else if (/android/.test(userAgent)) {
      setDeviceType('android');
    } else {
      setDeviceType('desktop');
    }

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for app installed
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Android/Desktop - use native install prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        logger.debug('User accepted the install prompt');
        setIsInstalled(true);
      }
      
      setDeferredPrompt(null);
    } else {
      // iOS or no prompt available - show manual instructions
      setShowInstructions(true);
    }
  };

  if (isInstalled) {
    return (
      <Card className="border-green-200 bg-green-50">
        <CardContent className="p-4">
          <div className="flex items-center space-x-3">
            <CheckCircle2 className="w-6 h-6 text-green-600" />
            <div>
              <p className="font-semibold text-green-900">App Installed!</p>
              <p className="text-sm text-green-700">Hines Time is installed on your device</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Smartphone className="w-6 h-6 text-blue-600" />
            <span>Install Hines Time</span>
          </CardTitle>
          <CardDescription>
            Install the app on your device for faster access and offline functionality
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={handleInstallClick}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Install App
          </Button>
          <p className="text-xs text-gray-500 text-center mt-3">
            Works like a native app - no app store required!
          </p>
        </CardContent>
      </Card>

      {/* Installation Instructions Dialog */}
      <Dialog open={showInstructions} onOpenChange={setShowInstructions}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Install Hines Time</DialogTitle>
            <DialogDescription>
              Follow these steps to install the app on your device
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {deviceType === 'ios' && (
              <>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-blue-900 mb-3">iPhone / iPad Instructions:</h4>
                  <ol className="space-y-3 text-sm text-blue-800">
                    <li className="flex items-start space-x-3">
                      <span className="font-bold min-w-[24px]">1.</span>
                      <div className="flex-1">
                        <p>Tap the <strong>Share</strong> button <Share className="w-4 h-4 inline text-blue-600" /> at the bottom of Safari</p>
                      </div>
                    </li>
                    <li className="flex items-start space-x-3">
                      <span className="font-bold min-w-[24px]">2.</span>
                      <div className="flex-1">
                        <p>Scroll down and tap <strong>"Add to Home Screen"</strong> <Plus className="w-4 h-4 inline text-blue-600" /></p>
                      </div>
                    </li>
                    <li className="flex items-start space-x-3">
                      <span className="font-bold min-w-[24px]">3.</span>
                      <div className="flex-1">
                        <p>Tap <strong>"Add"</strong> in the top right corner</p>
                      </div>
                    </li>
                    <li className="flex items-start space-x-3">
                      <span className="font-bold min-w-[24px]">4.</span>
                      <div className="flex-1">
                        <p>Hines Time will appear on your home screen!</p>
                      </div>
                    </li>
                  </ol>
                </div>
                <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> This only works in Safari browser. If you're using Chrome, open hinestime.com in Safari first.
                  </p>
                </div>
              </>
            )}

            {deviceType === 'android' && (
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-green-900 mb-3">Android Instructions:</h4>
                <ol className="space-y-3 text-sm text-green-800">
                  <li className="flex items-start space-x-3">
                    <span className="font-bold min-w-[24px]">1.</span>
                    <div className="flex-1">
                      <p>Tap the <strong>menu</strong> button <Chrome className="w-4 h-4 inline text-green-600" /> (three dots) in Chrome</p>
                    </div>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="font-bold min-w-[24px]">2.</span>
                    <div className="flex-1">
                      <p>Tap <strong>"Add to Home screen"</strong> or <strong>"Install app"</strong></p>
                    </div>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="font-bold min-w-[24px]">3.</span>
                    <div className="flex-1">
                      <p>Tap <strong>"Install"</strong> or <strong>"Add"</strong></p>
                    </div>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="font-bold min-w-[24px]">4.</span>
                    <div className="flex-1">
                      <p>Hines Time will appear on your home screen!</p>
                    </div>
                  </li>
                </ol>
              </div>
            )}

            {deviceType === 'desktop' && (
              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="font-semibold text-purple-900 mb-3">Desktop Instructions:</h4>
                <ol className="space-y-3 text-sm text-purple-800">
                  <li className="flex items-start space-x-3">
                    <span className="font-bold min-w-[24px]">1.</span>
                    <div className="flex-1">
                      <p>Click the <strong>install icon</strong> <Download className="w-4 h-4 inline text-purple-600" /> in the address bar</p>
                    </div>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="font-bold min-w-[24px]">2.</span>
                    <div className="flex-1">
                      <p>Or go to <strong>Chrome menu → Install Hines Time</strong></p>
                    </div>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="font-bold min-w-[24px]">3.</span>
                    <div className="flex-1">
                      <p>Hines Time will open in its own window!</p>
                    </div>
                  </li>
                </ol>
              </div>
            )}
          </div>

          <Button
            onClick={() => setShowInstructions(false)}
            variant="outline"
            className="w-full mt-4"
          >
            <X className="w-4 h-4 mr-2" />
            Close
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InstallPWA;
